<!--
@component
Action or link row inside a Dropdown, with keyboard navigation and selected/disabled states.

@figma https://www.figma.com/design/xb1BaksrwtGhe3JHzX1BbT/Design-System?node-id=2376-7300
@see Dropdown
-->
<script lang="ts">
  import Check from "@lucide/svelte/icons/check";
  import clsx from "clsx";
  import type { Snippet } from "svelte";
  import { getContext } from "svelte";

  import { DROPDOWN_CONTEXT, type DropdownContext } from "./Dropdown.svelte";

  /**
   * Props for link-based dropdown items.
   * Use for navigation - SEO-friendly, right-click works, no JS needed.
   *
   * When using href + onclick together:
   * - The href defines the destination (enables right-click, SEO, progressive enhancement)
   * - The onclick can intercept and call event.preventDefault() to cancel navigation
   * - DO NOT call goto() in onclick when href is present - let the browser handle navigation
   */
  type LinkProps = {
    href?: string;
    newTab?: boolean;
    download?: string;
  };

  // Needs to be passed if you want to support keyboard navigation of dropdown items.
  type KeyboardNavProps = { itemIndex?: number };

  type Props = {
    selected?: boolean;
    subtitle?: string;
    disabled?: boolean;
    class?: string;
    icon?: Snippet;
    /** Right-aligned content after the label, e.g. a chip. */
    trailing?: Snippet;
    children: Snippet;
    onclick?: () => void;
  } & LinkProps &
    KeyboardNavProps;

  let {
    selected = false,
    subtitle = "",
    disabled = false,
    class: classOverride = "",
    icon,
    trailing,
    children,
    onclick,
    itemIndex,
    ...hrefProps
  }: Props = $props();

  let dropdownContext = getContext<DropdownContext>(DROPDOWN_CONTEXT);
  const onmouseover = () => {
    if (itemIndex === undefined) return;
    // This is the key purpose of `lastNavType` state. We want to prevent focusing an item
    // based on inadvertant mouseover position when the user navs with keys.
    if (dropdownContext.lastNavType === "key") return;
    dropdownContext.focusedIndex = itemIndex;
  };

  let isFocused = $derived(
    itemIndex !== undefined && dropdownContext.focusedIndex === itemIndex,
  );

  let menuItemClass = $derived(
    clsx("menu-item", classOverride, {
      disabled,
      selected,
      "menu-item-focused": isFocused,
      "menu-item-without-index": itemIndex === undefined,
    }),
  );

  /** Ref to the focused element so we can scroll it into view and handle key events. */
  let actionEl = $state<HTMLElement | null>(null);
  const scrollIntoView = (block: "nearest" | "center") => {
    actionEl?.scrollIntoView({
      block,
      behavior: "auto",
    });
  };

  $effect(() => {
    if (!isFocused) return;

    // Keep focused item visible after ArrowUp/ArrowDown
    scrollIntoView("nearest");

    const keyHandler = async (e: KeyboardEvent) => {
      if (e.key !== "Enter") return;
      e.preventDefault();
      e.stopImmediatePropagation();
      actionEl?.click();
      dropdownContext.closeMenu?.();
    };
    window.addEventListener("keydown", keyHandler, true);
    return () => window.removeEventListener("keydown", keyHandler, true);
  });

  // When the dropdown is first opened, scroll the selected item into view
  // and set the focused index to the selected item
  $effect(() => {
    if (!selected) return;
    if (itemIndex === undefined) return;
    dropdownContext.focusedIndex = itemIndex;
    scrollIntoView("center");
  });
</script>

{#snippet content()}
  {@render icon?.()}
  <div class="min-w-0 overflow-hidden">
    <span class="label">{@render children()}</span>
    {#if subtitle}
      <p class="subtitle">{subtitle}</p>
    {/if}
  </div>
  {#if selected || trailing}
    <div class="ml-auto flex items-center gap-x-2">
      {@render trailing?.()}
      {#if selected}
        <Check size={20} class="text-accent" />
      {/if}
    </div>
  {/if}
{/snippet}

<!-- svelte-ignore a11y_no_static_element_interactions -->
<!-- svelte-ignore a11y_mouse_events_have_key_events -->
<div {onmouseover}>
  {#if "href" in hrefProps && hrefProps.href !== undefined}
    {@const href = hrefProps.href}
    {@const newTab = hrefProps.newTab}
    {@const download = hrefProps.download}
    <a
      href={disabled ? undefined : href}
      target={newTab ? "_blank" : undefined}
      rel={newTab ? "noopener noreferrer" : undefined}
      {download}
      {onclick}
      class={menuItemClass}
      bind:this={actionEl}
    >
      {@render content()}
    </a>
  {:else}
    <button
      type="button"
      class={menuItemClass}
      {disabled}
      {onclick}
      bind:this={actionEl}
    >
      {@render content()}
    </button>
  {/if}
</div>

<style lang="postcss">
  @reference "$app.css";

  .menu-item {
    /* width (not min-width) so the item never outgrows its menu: <button>
       elements refuse to shrink below the min-content of their text in
       Blink/WebKit, and min-width is only a floor. */
    @apply flex w-full items-center gap-x-2 rounded-md px-2 py-1.5 text-left;
  }

  /* Keyboard focus is virtual (driven by `focusedIndex` → `.menu-item-focused`),
     but melt still puts real DOM focus on the first item. Suppress that native
     ring so it doesn't linger on the first row while the highlight moves. */
  .menu-item:focus-visible {
    @apply outline-hidden;
  }

  .disabled {
    @apply cursor-default opacity-50;
  }

  .menu-item.selected {
    @apply bg-primary-selected;
  }

  .menu-item.selected .label {
    @apply text-primary-selected;
  }

  .menu-item-without-index:not(.selected):hover {
    @apply bg-primary-hover;
  }

  .menu-item-focused {
    @apply bg-primary-focus;
  }

  /* block so the ellipsis styles apply (they are inert on inline spans) */
  .label {
    @apply block grow overflow-hidden text-sm text-ellipsis whitespace-nowrap text-primary;
  }

  .subtitle {
    @apply mt-1 w-[26ch] max-w-full text-sm text-secondary;
  }
</style>
